import { LocalizationService } from '@abp/ng.core';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { Component, EventEmitter, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { map, take } from 'rxjs';
import { FormGroupStatus } from 'src/core/constants/form/form-group-status.constant';
import { LLMProvider } from 'src/core/models/generic-lookup-type/llm/llm-provider.glt';
import { GenericLookupDto } from 'src/core/models/generic-lookup/generic-lookup.dto';
import { LLMConfigurationDto } from 'src/core/models/llm/llm-configuration.dto';
import { CrudService } from 'src/core/services/crud/crud.service';
import { LLMConfigurationService } from 'src/core/services/llm/llm-configuration.service';
import { GenericLookupTypeState } from 'src/core/states/generic-lookup-type/generic-lookup-type.state';
import { LLMConfigurationNameValidator } from 'src/core/validators/llm/llm-configuration-name.validator';
import { FormButtonConfiguration } from 'src/ca-shared/form/form-button-configuration.model';
import { LLMProviderModelListDto } from 'src/core/models/llm/llm-provider-model-list.dto';

@Component({
  selector: 'ca-llm-configuration-form',
  templateUrl: './llm-configuration-form.component.html',
  styleUrl: './llm-configuration-form.component.scss',
  standalone: false,
})
export class LLMConfigurationFormComponent {
  @Output()
  saved: EventEmitter<{
    id: number;
  }> = new EventEmitter();

  @Output()
  deleted: EventEmitter<{
    id: number;
  }> = new EventEmitter();

  @Output()
  closed = new EventEmitter();

  get isOpenAIProvider() {
    return this.llmConfigurationForm?.get('llmProviderId').value == LLMProvider.openAI;
  }

  get isAzureProvider() {
    return this.llmConfigurationForm?.get('llmProviderId').value == LLMProvider.azure;
  }

  get formButtonConfiguration(): FormButtonConfiguration {
    const result: FormButtonConfiguration = {
      delete: {
        name: this.localizationService.instant('AbpUi::Delete'),
        visible: this.llmConfiguration?.id > 0,
        disabled: false,
        tooltip: this.localizationService.instant('AbpUi::Delete'),
      },
    };

    return result;
  }

  items: [];
  llmConfigurationForm: FormGroup;
  llmConfiguration: LLMConfigurationDto;
  processing = false;
  columnsToDisplay: string[] = ['lookupValue', 'delete'];
  llmConfigurationNameValidator: LLMConfigurationNameValidator;
  validators: Validators;
  valid = true;
  invalidIndex: number;
  invalidErrorMessage: string;
  processingMessage = this.localizationService.instant('AbpUi::ProcessingWithThreeDot');
  llmProviders: GenericLookupDto[] = [];
  openAIModels: string[] = [];

  constructor(
    private service: CrudService,
    private fb: FormBuilder,
    private store: Store,
    private confirmationService: ConfirmationService,
    private localizationService: LocalizationService,
    private llmConfigurationService: LLMConfigurationService,
    private toastr: ToasterService
  ) {
    this.llmConfigurationNameValidator = new LLMConfigurationNameValidator(
      this.llmConfigurationService
    );

    this.store
      .select(GenericLookupTypeState.getGenericLookups)
      .pipe(map(filterFn => filterFn(LLMProvider)))
      .subscribe(result => {
        this.llmProviders = result;
      });

    this.service
      .abpGetById<LLMProviderModelListDto>(LLMProviderModelListDto, 'openai')
      .pipe(take(1))
      .subscribe(data => {
        this.openAIModels = data.models;
      });

    this.llmConfigurationForm = this.fb.group({
      id: [0],
      llmProviderId: [null, Validators.required],
      generativeAIModel: [null, { validators: [this.providerBasedValidator(LLMProvider.openAI)] }],
      apiVersion: [
        null,
        { validators: [this.providerBasedValidator(LLMProvider.azure), Validators.maxLength(200)] },
      ],
      resourceName: [
        null,
        { validators: [this.providerBasedValidator(LLMProvider.azure), Validators.maxLength(200)] },
      ],
      deploymentName: [
        null,
        { validators: [this.providerBasedValidator(LLMProvider.azure), Validators.maxLength(200)] },
      ],
      apiKey: [null, { validators: [this.apiKeyValidator(), Validators.maxLength(200)] }],
      name: [
        null,
        {
          validators: [
            Validators.required,
            Validators.pattern(/^[a-zA-Z0-9_-]{1,200}$/),
            Validators.maxLength(200),
          ],
          asyncValidators: [this.llmConfigurationNameValidator],
        },
      ],
    });
  }

  ngOnInit() {}

  load(id: number | null) {
    this.items = [];
    this.resetForm();
    this.llmConfigurationNameValidator.id = id;
    if (id == null) {
      this.llmConfiguration = {
        id: 0,
        name: null,
        lastModificationTime: null,
        llmProviderName: null,
        apiKey: null,
        createdBy: null,
        resourceName: null,
        apiVersion: null,
        deploymentName: null,
        generativeAIModel: null,
        llmProviderId: null,
      };

      this.llmConfigurationForm.patchValue(this.llmConfiguration);
    } else {
      this.processingMessage = this.localizationService.instant('CA::Loading') + '...';
      this.processing = true;

      this.service
        .getById<LLMConfigurationDto>(LLMConfigurationDto, id)
        .pipe(take(1))
        .subscribe(data => {
          this.llmConfiguration = data;
          this.llmConfigurationForm.patchValue(this.llmConfiguration);
          this.llmConfigurationForm.get('llmProviderId').updateValueAndValidity();

          this.processing = false;
          this.processingMessage = this.localizationService.instant(
            'AbpUi::ProcessingWithThreeDot'
          );
        });
    }
  }

  closePanel() {
    this.llmConfigurationForm.reset();

    this.closed.emit();
  }

  onSubmitForm() {
    var providerID = this.llmConfigurationForm?.get('llmProviderId').value;

    if (
      this.processing ||
      this.llmConfigurationForm.invalid ||
      this.llmConfigurationForm.disabled
    ) {
      return;
    }

    this.processing = true;

    if (this.llmConfigurationForm.valid) {
      this.saveLLMConfiguration();
    } else if (this.llmConfigurationForm.pending) {
      const subscription = this.llmConfigurationForm.statusChanges.subscribe(status => {
        if (status === FormGroupStatus.VALID) {
          subscription.unsubscribe();
          this.saveLLMConfiguration();
        } else if (status === FormGroupStatus.PENDING) {
          this.processing = true;
        } else {
          subscription.unsubscribe();
          this.processing = false;
        }
      });
    }
  }

  saveLLMConfiguration() {
    const llmConfiguration: LLMConfigurationDto = this.llmConfigurationForm.value;

    llmConfiguration.id = llmConfiguration.id == null ? 0 : llmConfiguration.id;
    llmConfiguration.apiKey = llmConfiguration.apiKey?.trim();
    this.service
      .save<LLMConfigurationDto>(LLMConfigurationDto, llmConfiguration)
      .pipe(take(1))
      .subscribe({
        next: data => {
          this.saved.emit({ id: data.id });
          this.llmConfigurationForm.reset();
          this.processing = false;
        },
        error: () => {
          this.processing = false;
          return null;
        },
      });
  }

  deleteLLMConfiguration(eventArgs) {
    this.processing = true;

    this.service
      .delete<LLMConfigurationDto>(LLMConfigurationDto, this.llmConfiguration.id)
      .pipe(take(1))
      .subscribe({
        next: data => {
          this.processing = false;
          this.deleted.emit({ id: this.llmConfiguration.id });
          this.llmConfigurationForm.reset();
        },
        error: () => {
          this.processing = false;
          return null;
        },
      });
  }

  resetForm() {
    this.llmConfigurationForm?.reset();
  }

  confirmDeletion(eventArgs, name) {
    this.confirmationService
      .warn(
        'Settings::LLMConfiguration:DeletionConfirmationMessage',
        'Settings::LLMConfiguration:DeletionConfirmationTitle',
        {
          messageLocalizationParams: [name],
          yesText: '::Delete',
        }
      )
      .pipe(take(1))
      .subscribe((status: Confirmation.Status) => {
        if (status === Confirmation.Status.confirm) {
          this.deleteLLMConfiguration(eventArgs);
        }
      });
  }

  providerBasedValidator(providerId) {
    return (control: AbstractControl) => {
      var selectedProviderID = this.llmConfigurationForm?.get('llmProviderId').value;
      if (selectedProviderID === providerId && !control.value) {
        return { required: true };
      }
    };
  }

  apiKeyValidator() {
    return (control: AbstractControl) => {
      var id = this.llmConfigurationForm?.get('id').value;
      var selectedProviderID = this.llmConfigurationForm?.get('llmProviderId').value;

      if (
        [LLMProvider.openAI, LLMProvider.azure].filter(x => x == selectedProviderID).length > 0 &&
        control.value === null &&
        id == 0
      ) {
        return { required: true };
      }
    };
  }

  onProviderChange(event: any) {
    if (event == LLMProvider.openAI) {
      if (this.openAIModels.length > 0) {
        this.llmConfigurationForm.get('generativeAIModel').setValue(this.openAIModels[0]);
      }
    }

    this.llmConfigurationForm.get('generativeAIModel').updateValueAndValidity();
    this.llmConfigurationForm.get('apiVersion').updateValueAndValidity();
    this.llmConfigurationForm.get('resourceName').updateValueAndValidity();
    this.llmConfigurationForm.get('deploymentName').updateValueAndValidity();
    this.llmConfigurationForm.get('apiKey').updateValueAndValidity();
  }
}
